import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import { DateTimePicker } from '@material-ui/pickers'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Divider from '@material-ui/core/Divider'
import Checkbox from '@material-ui/core/Checkbox'
import moment, { Moment } from 'moment'
import { InputLabel, Chip } from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import InputAdornment from '@material-ui/core/InputAdornment' // Agregado
import { Calendar } from 'lib/lucide'

import { Dialog, snackbar, Notice } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { ERRORS, STRINGS } from 'consts'
import { Tag } from 'features/Main/Tags/types'

import { createNotification } from '../api'
import { CreateNotificationPayload, Notification, CreateNotificationResponse } from '../types'
import { useStyles } from './styles'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 50,
      message: 'No puede superar los 50 caracteres',
    },
  ],
  body: [
    {
      validate: (body: string) => body.length <= 192,
      message: 'No puede superar los 192 caracteres',
    },
  ],
}

interface DuplicateNotificationDialogProps {
  notification: Notification
  handleClose: () => void
  onDone: (newNotification: Notification) => void
  tags: Tag[]
  calcUsersByTags: (tagsSelected: Tag[], allTagsAreRequired?: boolean) => Promise<number>
}

const DuplicateNotificationDialog: React.FC<DuplicateNotificationDialogProps> = ({
  notification,
  handleClose,
  onDone,
  tags,
  calcUsersByTags,
}) => {
  const [title, setTitle] = useState(notification.title)
  const [body, setBody] = useState(notification.body)
  const [scheduled, setScheduled] = useState<Moment | null>(moment(notification.scheduled))
  const data = { title, body }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(data, rules)
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors
  const [tagsSelected, setTagsSelected] = useState<any[]>([])
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(notification.allTagsAreRequired)
  const [usersByTags, setUsersByTags] = useState(0)

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = async e => {
    const total = await calcUsersByTags(tagsSelected, !allTagsAreRequired)
    await setAllTagsAreRequired(!allTagsAreRequired)
    setUsersByTags(total)
  }

  const handleSetTags = async (e: any[]) => {
    setTagsSelected(e)
    const totalUsers = await calcUsersByTags(e)
    setUsersByTags(totalUsers)
  }

  const [createNotificationApi, isLoading] = useApiCall<
    CreateNotificationPayload,
    CreateNotificationResponse
  >(createNotification)

  const handleDateChange = (newDate: Moment | null) => {
    setScheduled(newDate)
  }

  const handleSubmit = async () => {
    if (!scheduled) return
    const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)
    const data = {
      title,
      body,
      scheduled: scheduled.toISOString(),
      tagIds: tagsSelectedId,
      allTagsAreRequired,
    }
    try {
      const { notification: newNotification } = await createNotificationApi(data)
      onDone(newNotification)
    } catch (err) {
      console.error(err)
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  useEffect(() => {
    if (notification.tags) {
      const tagsSelected = notification.tags.map(el => ({ label: el.title, value: el.id }))
      setTagsSelected(tagsSelected)
    }
    const getUsersByTags = async () => {
      return await calcUsersByTags(tags, allTagsAreRequired)
    }
    getUsersByTags().then(el => {
      setUsersByTags(el)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const classes = useStyles()

  return (
    <Dialog
      title="Duplicar notificación"
      isOpen
      onCancel={handleClose}
      showActions
      okButtonText="Duplicar notificación"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      isLoading={isLoading}
      contentStyle={{ minWidth: 500 }}
      overflow
      style={{ minHeight: '40%' }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Título"
            required
            onChange={e => setTitle(e.target.value)}
            value={title}
            error={errors.title.hasError}
            helperText={errors.title.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Mensaje"
            required
            onChange={e => setBody(e.target.value)}
            value={body}
            error={errors.body.hasError}
            helperText={errors.body.message}
          />
        </Grid>
        <Grid item xs={12}>
          <DateTimePicker
            value={scheduled}
            onChange={handleDateChange}
            autoOk
            disablePast
            format={STRINGS.DATE_TIME_FORMAT}
            initialFocusedDate={new Date()}
            disabled={isLoading}
            label="Programado"
            required
            TextFieldComponent={props => (
              <TextField
                {...props}
                fullWidth
                InputProps={{
                  ...props.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Calendar size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel>Tag</InputLabel>

          {/* Solo se muestra si hay al menos un tag seleccionado */}
          {tagsSelected.length > 0 && (
            <div
              style={{
                minHeight: '40px',
                maxHeight: '120px',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
              }}
            >
              {tagsSelected.map(tag => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  onDelete={() => handleSetTags(tagsSelected.filter(t => t.value !== tag.value))}
                  color="primary"
                />
              ))}
            </div>
          )}

          <MultiSelect
            options={tagsForSelect}
            value={tagsSelected}
            onChange={setTagsSelected}
            labelledBy="Select"
            hasSelectAll={false}
            valueRenderer={() => null} // 🔹 Evita que los valores aparezcan dentro del input,
            className={classes.multiSelectChild}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox onChange={handleAllTagsAreRequiredChange} checked={allTagsAreRequired} />
            }
            label="Sólo usuarios con todos los tags seleccionados"
          />
        </Grid>
        <Grid item xs={12}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={12}>
          <Notice>Esta acción impactará a {usersByTags} usuarios</Notice>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { DuplicateNotificationDialog }
